import { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';
import classes from 'classnames';
import Logout from '@/assets/icons/logout.svg?react';
import Logo from '@/assets/icons/logo.svg?react';
import * as R from '@/Router/routes';
import Link from '@/components/Link/Link';
import styles from './Sidebar.module.scss';
import { MenuItem } from '@/components/Layouts/components/MobileHeader/MobileMenu';
import useEventCollector from '@/hooks/context/EventCollector/useEventCollector';
import ButtonEvent from '@/hooks/context/EventCollector/events/ButtonEvent';
import LinkEvent from '@/hooks/context/EventCollector/events/LinkEvent';

export type Props = {
  className?: string;
  wrapperClassName?: string;
  active: string;
  onSelect?: (url: string) => void;
  menuItems: MenuItem[];
  onLogout?: () => void;
  pending?: boolean;
  logoLinkUrl?: string;
};

const SidebarMemo = memo(function Sidebar({
  className,
  wrapperClassName,
  active,
  onSelect,
  menuItems,
  onLogout,
  pending,
  logoLinkUrl = R.ACTIVE_LOAN,
}: Props) {
  const { t } = useTranslation('menu');
  const { track } = useEventCollector();

  const [isSubMenuOpen, setSubMenuOpen] = useState(false);

  const handleToggleSubMenu = useCallback((item: string) => {
    setSubMenuOpen((prev) => {
      track(
        new ButtonEvent({
          description: 'submenu toggle',
          content: item,
        }),
      );

      return !prev;
    });
  }, []);

  const handleClick = (menu: MenuItem) => {
    if (onSelect && menu && menu.url) {
      onSelect(menu.url);
      track(
        new LinkEvent({
          description: 'menu link',
          content: menu.url,
        }),
      );
    }
  };

  useEffect(() => {
    if (active.indexOf('repayment') !== -1) {
      setSubMenuOpen(true);
    }
  }, [active]);

  return (
    <div className={wrapperClassName} data-test="sidebar">
      <Link to={logoLinkUrl} dataTest="sidebar-logo-link">
        <Logo data-test="sidebar-logo" />
      </Link>
      <div
        className={classes(className, styles.container)}
        data-test="sidebar-menu"
      >
        {menuItems.map((menu, index) => {
          const preload = () => {
            menu.preload?.();
          };

          const onSubmenuClick = () => {
            if (!menu.disabled) handleToggleSubMenu(menu.title);
          };

          if (menu.subMenu) {
            return (
              <div
                role="button"
                className={classes([
                  styles.item,
                  styles.fleOne,
                  menu.disabled && styles.disabled,
                ])}
                onClick={onSubmenuClick}
                onKeyDown={onSubmenuClick}
                key={index}
                data-test={`sidebar-menu-item-${index}`}
                tabIndex={0}
              >
                <div className={styles.flex}>
                  <div
                    className={classes(
                      styles.icon,
                      (active === menu.url ||
                        menu.additionalActiveState?.includes(active)) &&
                        styles['selected-icon'],
                      menu.disabled && styles.disabled,
                    )}
                    data-test={`sidebar-menu-item-icon-${index}`}
                  >
                    {menu.icon}
                  </div>
                  <div
                    className={classes([
                      styles.title,
                      menu.disabled && styles.disabled,
                    ])}
                    data-test={`sidebar-menu-item-title-${index}`}
                  >
                    {menu.title}
                  </div>
                </div>
                {isSubMenuOpen && !menu.disabled && (
                  <div>
                    <div className={styles.subMenuCard}>
                      {menu.subMenu?.map((subMenuItem, i) => {
                        if (!subMenuItem) return;

                        return (
                          <RouterLink
                            data-test={`sidebar-${subMenuItem.dataTest}`}
                            className={classes([
                              styles.item,
                              styles.link,
                              subMenuItem.url === active && styles.active,
                              subMenuItem.disabled && styles.disabled,
                            ])}
                            onClick={() => handleClick(menu)}
                            to={subMenuItem.url ?? ''}
                            key={`${subMenuItem.url}-${i}`}
                          >
                            <div
                              className={classes(styles.title)}
                              data-test={`sidebar-menu-item-submenu-${index}`}
                            >
                              {subMenuItem.title}
                            </div>
                          </RouterLink>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          }
          return (
            <RouterLink
              data-test={`sidebar-${menu.dataTest}`}
              className={classes(
                styles.item,
                (active === menu.url ||
                  menu.additionalActiveState?.includes(active)) &&
                  styles.selected,
                menu.disabled && styles.disabled,
              )}
              onClick={() => handleClick(menu)}
              to={menu.url ?? ''}
              key={`${menu.url}-${index}`}
              onMouseEnter={preload}
            >
              <div
                className={classes(
                  styles.icon,
                  (active === menu.url ||
                    menu.additionalActiveState?.includes(active)) &&
                    styles['selected-icon'],
                )}
              >
                {menu.icon}
              </div>
              <div className={classes(styles.title)}>{menu.title}</div>
            </RouterLink>
          );
        })}
      </div>
      <Link
        size="s"
        onClick={onLogout}
        className={classes(styles.logout, pending && styles.disabled)}
      >
        <span className={styles.text}>{t('logout')}</span>
        <span className={styles['logout-icon']}>
          <Logout />
        </span>
      </Link>
    </div>
  );
});

export default SidebarMemo;
